import React, { useEffect, useMemo, useState } from "react";
import { TopBar } from "@/components/TopBar";
import { EmptyState } from "@/components/EmptyState";
import { RichText } from "@/components/RichText";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useBackButton } from "@/lib/hooks";
import { useConfig } from "@/lib/useConfig";
import { faqFeedback, faqViewed, fetchFaq, type FaqCategory, type FaqItem } from "@/lib/api";
import { haptic, openExternalLink } from "@/lib/telegram";

const CATS: FaqCategory[] = ["order", "delivery", "returns", "coffee", "business", "other"];
const icon = (d: string) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
    <path d={d} />
  </svg>
);

/** "Частые вопросы": search, categories, accordion, "helpful?" and a way to reach a human. */
export function FaqPage({ category }: { category?: string }) {
  const { t, locale } = useI18n();
  const { goBack } = useRouter();
  const { botUsername } = useConfig();
  useBackButton(goBack);
  const [items, setItems] = useState<FaqItem[] | null>(null);
  const [cat, setCat] = useState<FaqCategory | "all">((CATS as string[]).includes(category ?? "") ? (category as FaqCategory) : "all");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [voted, setVoted] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchFaq().then(setItems).catch(() => setItems([]));
  }, [locale]);

  const present = useMemo(() => CATS.filter((c) => items?.some((i) => i.category === c)), [items]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (items ?? []).filter((i) => (cat === "all" || i.category === cat) && (!q || i.question.toLowerCase().includes(q) || i.answer.toLowerCase().includes(q)));
  }, [items, cat, query]);

  const toggle = (id: string) => {
    haptic("selection");
    setOpen((cur) => {
      if (cur !== id) faqViewed(id);
      return cur === id ? null : id;
    });
  };

  return (
    <>
      <TopBar title={t("faq.title")} onBack={goBack} showCart={false} />
      <main className="page page--no-nav">
        <label className="faq-search">
          {icon("M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14ZM20 20l-3.5-3.5")}
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("faq.search")} />
        </label>
        {present.length > 1 && (
          <div className="segmented" style={{ marginBottom: 10 }}>
            <button className="chip" data-active={cat === "all"} onClick={() => setCat("all")}>
              {t("faq.all")}
            </button>
            {present.map((c) => (
              <button key={c} className="chip" data-active={cat === c} onClick={() => setCat(c)}>
                {t(`faq.cats.${c}`)}
              </button>
            ))}
          </div>
        )}

        {items === null ? (
          <p>{t("common.loading")}</p>
        ) : shown.length === 0 ? (
          <EmptyState art="search" title={query ? t("faq.nothing") : t("faq.empty")} />
        ) : (
          <div className="faq-list">
            {shown.map((i) => (
              <div key={i.id} className="faq-item">
                <button className="faq-q" aria-expanded={open === i.id} onClick={() => toggle(i.id)}>
                  <span>{i.question}</span>
                  {open === i.id ? icon("M4 12h16") : icon("M12 4v16M4 12h16")}
                </button>
                {open === i.id && (
                  <div className="faq-a">
                    <RichText text={i.answer} />
                    <div className="faq-useful">
                      {voted[i.id] === undefined ? (
                        <>
                          {t("faq.helpful")}
                          <button onClick={() => { setVoted({ ...voted, [i.id]: true }); faqFeedback(i.id, true).catch(() => {}); }}>👍 {t("faq.yes")}</button>
                          <button onClick={() => { setVoted({ ...voted, [i.id]: false }); faqFeedback(i.id, false).catch(() => {}); }}>{t("faq.no")}</button>
                        </>
                      ) : (
                        <span>{voted[i.id] ? t("faq.thanks") : t("faq.sorry")}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {botUsername && (
          <div className="faq-help">
            <strong>{t("faq.notFound")}</strong>
            <span>{t("faq.notFoundHint")}</span>
            <button className="btn btn--primary" onClick={() => openExternalLink(`https://t.me/${botUsername}`)}>
              {t("faq.writeUs")}
            </button>
          </div>
        )}
      </main>
    </>
  );
}

/** Small entry point used on checkout and the order page. */
export function FaqLink({ category, title, hint }: { category?: FaqCategory; title: string; hint?: string }) {
  const { navigate } = useRouter();
  return (
    <button className="faq-link" onClick={() => navigate({ name: "faq", category })}>
      <span>
        <b>{title}</b>
        {hint && <small>{hint}</small>}
      </span>
      <span aria-hidden="true">›</span>
    </button>
  );
}
